import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { TenantRedirect } from './components/tenant-redirect';
import { AuthGuard } from './guards/auth-guard';
import { ProcessingCompleteGuard } from './guards/processing-complete-guard';
import { ProfileGuard } from './guards/profile-guard';
import { SubscriptionGuard } from './guards/subscription-guard';
import { TenantGuard } from './guards/tenant-guard';

// Layouts
import { AuthLayout } from '@/components/layout/auth-layout';
import { BlankLayout } from '@/components/layout/blank-layout';
import { FullscreenLayout } from '@/components/layout/fullscreen-layout';
import { ProfileLayout } from '@/components/layout/profile-layout';
import { RootLayout } from '@/components/layout/root-layout';
import { SettingsLayout } from '@/components/layout/settings-layout';
import { WelcomeLayout } from '@/components/layout/welcome-layout';
// Providers — mounted inside the router, see AppProviders below
import { ApiProvider, RegionProvider, TenantProvider } from '@/context';
import { TenantRegistryProvider, useConfirmedTenantPersistence } from '@/context/tenant-registry-context';
// Legal pages (public) + the two legal gates (ESCLOUD-286/287)
import {
    DeclarationOfConformityPage,
    DeviceUseGate,
    EifuPage,
    EulaGate,
    EulaPage,
    LegalNoticePage,
    PrivacyPolicyPage,
} from '@/legal';
// Auth pages
import { LoginPage } from '@/pages/auth/login';
import { LogoutPage } from '@/pages/auth/logout';
// Protected pages
import { CasesPage } from '@/pages/cases';
import { ViewerPage } from '@/pages/cases/[caseId]/viewer';
import { TrashPage } from '@/pages/cases/trash';
import { CreateCaseWizard } from '@/pages/cases/wizard';
import { InvitePage } from '@/pages/invite';
import { PricingPage } from '@/pages/pricing';
import { ProfilePage } from '@/pages/profile';
import { AccountStepPage } from '@/pages/subscribe/account';
import { ActivatingIntentPage } from '@/pages/subscribe/activating';
import { PaymentPage } from '@/pages/subscribe/payment';
import { BillingPage } from '@/pages/tenants/[tenantId]/billing';
import { GatewaysPage } from '@/pages/tenants/[tenantId]/gateways';
import { MembersPage } from '@/pages/tenants/[tenantId]/members';
import { TenantSettingsPage } from '@/pages/tenants/[tenantId]/settings';
import { WelcomeProfilePage } from '@/pages/welcome/profile';

/**
 * App-wide providers, mounted as the router's root element.
 *
 * They live INSIDE the router because TenantProvider derives the current tenant
 * from the matched route — the URL is the authority for a tenant-scoped route,
 * and reading it during render is what keeps the guards, the `X-Tenant-Id`
 * header and the tenant-keyed queries in agreement on the first render after a
 * switch (ESCLOUD-269). Auth0 sits above the router and stays in App.
 */
function AppProviders() {
    return (
        <RegionProvider>
            <TenantProvider>
                <ApiProvider>
                    {/* Needs the API client, so it sits below ApiProvider; the
                        tenant context above it only resolves the id and region. */}
                    <TenantRegistryProvider>
                        <ConfirmedTenantPersistence />
                        <Outlet />
                    </TenantRegistryProvider>
                </ApiProvider>
            </TenantProvider>
        </RegionProvider>
    );
}

/**
 * Remembers the organization in the URL only once a regional API has vouched for
 * it — see useConfirmedTenantPersistence.
 */
function ConfirmedTenantPersistence() {
    useConfirmedTenantPersistence();
    return null;
}

interface IGatingStep {
    /** The component that decides, as mounted below. */
    readonly gate: ComponentType;
    readonly name: string;
    /** Where it is mounted, in the words of the route tree below. */
    readonly mountedAt: string;
    /** What it decides, and what it does when the answer is yes. */
    readonly gates: string;
    /**
     * What it does when the question cannot be answered — a failed query, a
     * token that carries no claim, a region that did not reply. Every gate has
     * one and they are deliberately not the same, so this is the column to read
     * before adding a gate or changing one.
     */
    readonly onUnanswerable: string;
    /**
     * Set only for a gate that is not a route element: the layouts that render
     * it. An overlay renders *beside* the page rather than instead of it, so it
     * cannot be a route element without blacking the page out while it loads.
     */
    readonly mountedIn?: readonly ComponentType[];
}

/**
 * The gating chain, in the order a request through the deepest route meets it
 * (ESCLOUD-288). This is the declaration; the route tree below is the mount, and
 * `router.spec.tsx` checks the two against each other so they cannot drift.
 *
 * Read this instead of reconstructing the chain from the guards: each guard's
 * own docblock says what *it* decides and points here for the rest, because
 * every partial restatement went stale the moment a gate was added.
 *
 * Email verification left the chain with the onboarding profile work
 * (ESCLOUD-333): it is now a banner on the affected pages and one of the
 * conditions the device-use dialog collects, not a route-level redirect.
 *
 * Note the EULA overlay's position. It is mounted by the shell layouts and the
 * welcome layout: the funnel screens render their own full-page shell and
 * match no layout route, which is what exempts them - a fact of the route tree
 * rather than a path allowlist inside the gate. A funnel screen moved under a
 * shell layout would gain the overlay, and `router.spec.tsx` names the exempt
 * set so that shows up as a failure.
 */
export const GATING_CHAIN: readonly IGatingStep[] = [
    {
        gate: AuthGuard,
        name: 'AuthGuard',
        mountedAt: 'the root of the protected route branch',
        gates: 'an authenticated Auth0 session; otherwise redirects to /login, keeping the target in location state',
        onUnanswerable:
            'while Auth0 is still resolving the session, a full-screen spinner — never a redirect',
    },
    {
        gate: EulaGate,
        name: 'EulaGate',
        mountedIn: [RootLayout, FullscreenLayout, WelcomeLayout],
        mountedAt:
            'the shell layouts and the welcome layout, as a blocking overlay over whatever page they render',
        gates: 'acceptance of the current EULA version; blocks with the full text and Accept / Decline',
        onUnanswerable:
            'fails OPEN: renders nothing, logs, and raises a persistent warning toast. Availability of clinical functions must not depend on the gate endpoint (SDS-CLI-015). The purchase funnel records the acceptance before payment is confirmed and the server logs, never refuses, a missing one, so this overlay at app entry is the catch',
    },
    {
        gate: TenantGuard,
        name: 'TenantGuard',
        mountedAt: 'each /tenants/:tenantId and /settings/tenant/:tenantId branch',
        gates: 'a workspace the regional APIs confirmed; a confirmed absence hands over to TenantRedirect',
        onUnanswerable:
            'renders the route on `unprovable`. This is a navigation decision, not an authorization one: the API re-checks membership on every request, which is what makes failing open safe',
    },
    {
        gate: SubscriptionGuard,
        name: 'SubscriptionGuard',
        mountedAt: 'under TenantGuard on every tenant branch',
        gates: 'a subscription that exists; a read-only state renders the route with a banner; a proven absence renders a support panel (a tenant is only ever created from an activated subscription, so this is legacy data, not a funnel to resume)',
        onUnanswerable:
            'a retry surface, never a redirect: an unproven absence must not push a paying tenant anywhere',
    },
    {
        gate: ProfileGuard,
        name: 'ProfileGuard',
        mountedAt:
            'under SubscriptionGuard on the device pages and the viewer; settings, billing, /profile and /welcome/profile deliberately outside',
        gates: 'a professional profile the server calls complete; otherwise redirects to /welcome/profile',
        onUnanswerable:
            'renders the route: the device-use gate below fails closed and the API refuses clinical calls regardless',
    },
    {
        gate: DeviceUseGate,
        name: 'DeviceUseGate',
        mountedAt:
            'the whole /tenants/:tenantId subtree and the viewer; settings and /profile deliberately outside, because that is where "Not now" lands',
        gates: 'a verified email address plus the qualification attestation and IFU acknowledgement, collected in one blocking dialog (the profile is collected on /welcome/profile)',
        onUnanswerable:
            'fails CLOSED: a retry surface. The server refuses the clinical endpoints with 403 attestation_required anyway',
    },
    {
        gate: ProcessingCompleteGuard,
        name: 'ProcessingCompleteGuard',
        mountedAt: 'the viewer only, innermost',
        gates: "the case's initial processing job having completed; otherwise shows its queued / processing / failed state",
        onUnanswerable: 'shows the case-status panel rather than an empty viewer',
    },
];

/**
 * Exported for `router.spec.tsx`, which asserts where the gates sit in the tree
 * — the device-use gate's subtree (ESCLOUD-287) and the EULA overlay's exempt
 * set (ESCLOUD-286). Both are *placement* controls, so they are checked against
 * the real route objects rather than a copy.
 */
export const routes = [
    // Public routes (no auth required)
    {
        element: <AuthLayout />,
        children: [
            // Superseded by public pricing (ESCLOUD-332): kept as a redirect so
            // old links still resolve.
            { path: '/get-started', element: <Navigate to="/pricing" replace /> },
            { path: '/login', element: <LoginPage /> },
            { path: '/logout', element: <LogoutPage /> },
        ],
    },
    // Public routes (no auth required)
    {
        element: <BlankLayout />,
        children: [
            // Public pricing: funnel v2 starts here (ESCLOUD-332)
            { path: '/pricing', element: <PricingPage /> },
            { path: '/invite', element: <InvitePage /> },
            // Legal documents — reachable without authentication (ESCLOUD-77/78)
            { path: '/eifu', element: <EifuPage /> },
            { path: '/declaration-of-conformity', element: <DeclarationOfConformityPage /> },
            { path: '/legal-notice', element: <LegalNoticePage /> },
            { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
            // Readable pre-acceptance: the onboarding EULA checkbox links here (ESCLOUD-286)
            { path: '/eula', element: <EulaPage /> },
        ],
    },

    // Protected routes (auth required)
    {
        element: <AuthGuard />,
        children: [
            // A redirect for stale links from the retired wizard (ESCLOUD-333);
            // the page itself no longer exists (ESCLOUD-334).
            { path: '/onboarding', element: <Navigate to="/pricing" replace /> },
            // Another redirect for stale links: the pre-wizard creation screen
            // lived here, so old links are pointed at the funnel v2 account step.
            { path: '/tenants/create', element: <Navigate to="/subscribe/account" replace /> },
            // Funnel v2 (ESCLOUD-332): payment precedes verification, so these sit
            // outside the clinical gates; they render OnboardingShell themselves.
            { path: '/subscribe/account', element: <AccountStepPage /> },
            { path: '/subscribe/payment', element: <PaymentPage /> },
            { path: '/subscribe/activating', element: <ActivatingIntentPage /> },
            // Post-activation welcome. Its own layout mounts the EULA overlay for invited members.
            {
                element: <WelcomeLayout />,
                children: [{ path: '/welcome/profile', element: <WelcomeProfilePage /> }],
            },
            // Fullscreen viewer (no sidebar/header). A shell layout, so
            // it carries the EULA overlay - see GATING_CHAIN.
            {
                element: <FullscreenLayout />,
                children: [
                    {
                        path: '/tenants/:tenantId/cases/:caseId/viewer',
                        element: <TenantGuard />,
                        children: [
                            {
                                element: <SubscriptionGuard />,
                                children: [
                                    // Opening a workflow is the device-use gate's
                                    // trigger (ESCLOUD-287): profile + attestation
                                    // are collected here on first clinical use.
                                    {
                                        element: <ProfileGuard />,
                                        children: [
                                            {
                                                element: <DeviceUseGate />,
                                                children: [
                                                    {
                                                        element: <ProcessingCompleteGuard />,
                                                        children: [{ index: true, element: <ViewerPage /> }],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            // Main app. A shell layout, so it carries the EULA overlay
            // for every page below it - see GATING_CHAIN.
            {
                element: <RootLayout />,
                children: [
                    { path: '/', element: <TenantRedirect /> },
                    // Profile pages (with in-page sidebar)
                    {
                        path: '/profile',
                        element: <ProfileLayout />,
                        children: [{ index: true, element: <ProfilePage /> }],
                    },
                    // Device pages (main content). Every route under
                    // /tenants/:tenantId sits behind the device-use gate
                    // (ESCLOUD-287): the case list and the trash carry
                    // patient identifiers and the server refuses them
                    // with 403 attestation_required, so there is no page
                    // to render before the gate is passed. Gating the
                    // subtree rather than the individual pages is what
                    // makes a page added here gated by where it lives.
                    // Un-attested users reach administration only -
                    // /settings/tenant/:tenantId and /profile below,
                    // both deliberately outside. "Not now" lands on
                    // settings; coming back here re-displays the dialog,
                    // which is intended.
                    {
                        path: '/tenants/:tenantId',
                        element: <TenantGuard />,
                        children: [
                            {
                                element: <SubscriptionGuard />,
                                children: [
                                    {
                                        element: <ProfileGuard />,
                                        children: [
                                            {
                                                element: <DeviceUseGate />,
                                                children: [
                                                    { index: true, element: <CasesPage /> },
                                                    { path: 'cases/new', element: <CreateCaseWizard /> },
                                                    { path: 'cases/trash', element: <TrashPage /> },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    // Settings pages (with in-page sidebar)
                    {
                        path: '/settings/tenant/:tenantId',
                        element: <TenantGuard />,
                        children: [
                            {
                                element: <SubscriptionGuard />,
                                children: [
                                    {
                                        element: <SettingsLayout />,
                                        children: [
                                            {
                                                index: true,
                                                element: <Navigate to="general" replace />,
                                            },
                                            { path: 'general', element: <TenantSettingsPage /> },
                                            { path: 'members', element: <MembersPage /> },
                                            { path: 'gateways', element: <GatewaysPage /> },
                                            { path: 'billing', element: <BillingPage /> },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

const router = createBrowserRouter([{ element: <AppProviders />, children: routes }]);

export function Router() {
    return <RouterProvider router={router} />;
}
